import React, { useEffect, useState } from "react";
import { ViewModal } from "./ViewModal";
import { formatNumber, formatBerries, formatRate } from "../game/formatters";
import { UPGRADES, getCurrentEraKey } from "../game/constants";

const StatCell = ({ label, value, sub, testId }) => (
  <div
    className="relative p-4 flex flex-col gap-1"
    style={{
      backgroundColor: "#111",
      border: "1px solid #1e1e1e",
      borderRadius: 4,
    }}
    data-testid={testId}
  >
    <span
      className="text-[10px] uppercase tracking-[0.3em] text-white/50"
      style={{ fontFamily: "Orbitron, sans-serif" }}
    >
      {label}
    </span>
    <span
      className="text-xl sm:text-2xl font-black text-white leading-tight"
      style={{ fontFamily: "Orbitron, sans-serif" }}
    >
      {value}
    </span>
    {sub && (
      <span className="text-xs text-white/50 mt-0.5" style={{ fontFamily: "Orbitron, sans-serif" }}>
        {sub}
      </span>
    )}
  </div>
);

// Formats an elapsed millisecond duration as "1d 3h 24m" style
const formatDuration = (ms) => {
  if (!ms || ms < 0) return "0s";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

export const StatsView = ({ open, onClose, state, production }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!open) return () => {};
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const totalUpgrades = Object.values(state.upgrades || {}).reduce((a, b) => a + b, 0);
  const erasUnlocked = Object.keys(state.seenEras || {}).length;
  const eraKey = getCurrentEraKey(state.upgrades);
  const currentUpgrade = UPGRADES.find((u) => u.eraKey === eraKey);
  const eraName = currentUpgrade ? currentUpgrade.name : "Empty Hands";
  const playTime = now - (state.createdAt || now);

  // Top upgrade by count
  const topEntry = Object.entries(state.upgrades || {}).sort((a, b) => b[1] - a[1])[0];
  const topUpgrade = topEntry ? UPGRADES.find((u) => u.id === topEntry[0]) : null;

  // Berries per click stat
  const perClick = state.perClick;

  return (
    <ViewModal
      open={open}
      onClose={onClose}
      title="Statistics"
      subtitle="Every metric of your wheel empire"
      testId="stats-view"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCell
          label="Wheels Built"
          value={formatNumber(state.wheelsBuilt)}
          sub="total clicks"
          testId="stat-wheels-built"
        />
        <StatCell
          label="Current Berries"
          value={formatBerries(state.berries)}
          sub={`+${formatRate(production)}/s`}
          testId="stat-berries-current"
        />
        <StatCell
          label="Berries Earned"
          value={formatBerries(state.totalBerriesEarned || 0)}
          sub="all-time total"
          testId="stat-berries-total"
        />
        <StatCell
          label="Peak Berries"
          value={formatBerries(state.highestBerries || state.berries || 0)}
          sub="max at once"
          testId="stat-berries-peak"
        />
        <StatCell
          label="Upgrades Owned"
          value={formatNumber(totalUpgrades)}
          sub={`${UPGRADES.length} types available`}
          testId="stat-total-upgrades"
        />
        <StatCell
          label="Eras Unlocked"
          value={`${erasUnlocked}/${UPGRADES.length}`}
          sub="historical progress"
          testId="stat-eras"
        />
        <StatCell
          label="Current Era"
          value={eraName}
          sub="active wheel"
          testId="stat-era-current"
        />
        <StatCell
          label="Berries Per Click"
          value={`+${formatRate(perClick)}`}
          sub="manual value"
          testId="stat-per-click"
        />
        <StatCell
          label="Time Played"
          value={formatDuration(playTime)}
          sub="since start"
          testId="stat-playtime"
        />
      </div>

      {topUpgrade && (
        <div
          className="mt-4 p-4 flex items-center justify-between"
          style={{ backgroundColor: "#111", border: "1px solid #1e1e1e", borderRadius: 4 }}
          data-testid="stat-top-upgrade"
        >
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.3em] text-white/50"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              Top Upgrade
            </div>
            <div
              className="text-lg font-black text-white mt-1 uppercase tracking-widest"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              {topUpgrade.name}
            </div>
          </div>
          <div className="text-right">
            <div
              className="text-2xl font-black"
              style={{ color: "#FFD400", fontFamily: "Orbitron, sans-serif" }}
            >
              x{topEntry[1]}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">
              +{formatRate(topUpgrade.production * topEntry[1])}/s
            </div>
          </div>
        </div>
      )}
    </ViewModal>
  );
};

export default StatsView;
